import { ProviderPhoto } from "@src/common/ProviderPhoto";
import { APP_ROUTES } from "@src/constants";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import type { Provider } from "@src/interfaces/provider.interface";
import ClientLogsService from "@src/services/client-logs.service";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { Link } from "@uhg-abyss/web/ui/Link";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { Skeleton } from "@uhg-abyss/web/ui/Skeleton";
import { Text } from "@uhg-abyss/web/ui/Text";
import type { FC } from "react";
import { useState } from "react";

interface ProviderTeamCardProps {
  provider: Provider;
  isPhotoLoading?: boolean;
  fillAvailableHeight?: boolean;
}

const PhotoContainer = styled("div", {
  width: "100%",
  maxWidth: "240px",
  height: "240px",
  borderRadius: "8px",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "$app.color.surface.container.primary",
  alignSelf: "center",
  variants: {
    hasProviderPhoto: {
      true: { backgroundColor: "transparent" },
    },
  },
});

const CardContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  minWidth: 0,
});

const ModalPhotoContainer = styled("div", {
  width: "84px",
  height: "84px",
  borderRadius: "50%",
  overflow: "hidden",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "$app.color.surface.container.primary",
  variants: {
    hasProviderPhoto: {
      true: { backgroundColor: "transparent" },
    },
  },
});

const ModalHeader = styled("div", {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "16px",
  marginBottom: "20px",
});

const ModalSection = styled("div", {
  marginBottom: "16px",
});

const CardActions = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const ProviderTeamCardSkeletonRoot = styled(Card, {
  maxWidth: "300px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "16px",
});

export const ProviderTeamCardSkeleton = () => {
  return (
    <ProviderTeamCardSkeletonRoot
      aria-hidden="true"
      data-testid="test-id-meet-the-team-card-skeleton"
    >
      <Skeleton width="100%" height="240px" />
      <Skeleton width="70%" height="22px" />
      <Skeleton width="45%" height="18px" />
      <Skeleton width="60%" height="18px" />
      <Skeleton width="100%" height="40px" />
    </ProviderTeamCardSkeletonRoot>
  );
};

export const ProviderTeamCard: FC<ProviderTeamCardProps> = ({
  provider,
  isPhotoLoading = false,
  fillAvailableHeight = false,
}) => {
  const { data, markRouteAsComplete, setData } = useApplicationContext();
  const navigate = useNavigateWithUTM();
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);
  const hasProviderPhoto = Boolean(provider.providerPhoto?.hasPhoto);
  const hasProviderBio = Boolean(provider.providerBio?.trim());

  const handleBookNow = () => {
    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "ProviderTeamCard.handleBookNow",
      `User clicked Book now for provider ${provider.providerId}`,
    );
    setData("selectedProvider", provider);
    setData("providerSelectionSource", APP_ROUTES.MEET_THE_TEAM);
    markRouteAsComplete(APP_ROUTES.MEET_THE_TEAM);
    navigate(APP_ROUTES.GET_STARTED);
  };

  const handleViewBioOpen = () => {
    ClientLogsService.logClick(
      data.userUuid || "unknown",
      "ProviderTeamCard",
      `obc-meet-the-team-${provider.providerId}-view-bio-link`,
      `View Bio Link for provider ${provider.fullName}`,
    );
    setIsBioModalOpen(true);
  };

  const handleBioModalClose = () => {
    ClientLogsService.logClick(
      data.userUuid || "unknown",
      "ProviderTeamCard",
      `obc-meet-the-team-${provider.providerId}-bio-modal-close`,
      `Bio Modal Close for provider ${provider.fullName}`,
    );
    setIsBioModalOpen(false);
  };

  return (
    <Card
      style={{ maxWidth: "300px", width: "100%", height: fillAvailableHeight ? "100%" : undefined }}
      data-testid={`test-id-provider-team-card-${provider.providerId}`}
    >
      <CardContent>
        <PhotoContainer hasProviderPhoto={hasProviderPhoto}>
          <ProviderPhoto
            providerPhoto={provider.providerPhoto}
            altText={`${provider.fullName} photo`}
            fallbackIconSize={200}
            fallbackIconStyle={{ width: "240px", height: "240px" }}
            imageTestId={`test-id-provider-team-photo-${provider.providerId}`}
            isLoading={isPhotoLoading}
          />
        </PhotoContainer>

        <Text
          css={{
            "abyss-text-root": {
              fontWeight: 700,
              color: "$app.color.text.primary",
              fontSize: "16px",
              lineHeight: "22px",
            },
          }}
          data-testid={`test-id-provider-name-${provider.providerId}`}
        >
          {provider.fullName}
        </Text>

        <Text
          css={{ "abyss-text-root": { color: "$app.color.text.secondary", fontSize: "14px" } }}
          data-testid={`test-id-provider-type-${provider.providerId}`}
        >
          {provider.providerType}
        </Text>

        <CardActions>
          <Link
            id={`obc-meet-the-team-${provider.providerId}-view-bio-link`}
            data-id={`obc-meet-the-team-${provider.providerId}-view-bio-link`}
            data-obc-label={`View Bio Link for provider ${provider.fullName}`}
            data-testid={`test-id-provider-team-view-bio-${provider.providerId}`}
            onClick={hasProviderBio ? handleViewBioOpen : undefined}
            aria-hidden={!hasProviderBio}
            tabIndex={hasProviderBio ? 0 : -1}
            css={{
              "abyss-link-root": {
                cursor: "pointer",
                background: "none",
                border: "none",
                padding: 0,
                display: "flex",
                alignItems: "center",
                gap: "4px",
                textDecoration: "none",
                visibility: hasProviderBio ? "visible" : "hidden",
                pointerEvents: hasProviderBio ? "auto" : "none",
                "&:hover": {
                  textDecoration: "none",
                },
              },
            }}
          >
            View bio
            <IconSymbol
              icon="chevron_right"
              size="sm"
              css={{
                visibility: hasProviderBio ? "visible" : "hidden",
              }}
            />
          </Link>
          <Button
            id={`obc-meet-the-team-${provider.providerId}-begin-scheduling-btn`}
            data-id={`obc-meet-the-team-${provider.providerId}-begin-scheduling-btn`}
            data-obc-label={`Begin Scheduling Button for provider ${provider.fullName}`}
            data-testid={`test-id-provider-team-begin-scheduling-${provider.providerId}`}
            size="md"
            aria-label="Begin scheduling"
            icon={{ icon: "calendar_add_on", position: "trailing", variant: "outlined" }}
            onClick={handleBookNow}
          >
            Begin scheduling
          </Button>
        </CardActions>
      </CardContent>

      <ModalDialog
        data-testid={`test-id-provider-team-bio-modal-${provider.providerId}`}
        data-id={`obc-meet-the-team-${provider.providerId}-bio-modal`}
        data-obc-label={`Bio Modal for provider ${provider.fullName}`}
        css={{
          "abyss-modal-base-header-container": { padding: "32px" },
          "abyss-modal-base-body": { padding: "32px" },
        }}
        isOpen={isBioModalOpen}
        onClose={handleBioModalClose}
      >
        <ModalHeader>
          <ModalPhotoContainer hasProviderPhoto={hasProviderPhoto}>
            <ProviderPhoto
              providerPhoto={provider.providerPhoto}
              altText={`${provider.fullName} photo`}
              fallbackIconSize={64}
              fallbackIconStyle={{ width: "84px", height: "84px" }}
              isLoading={isPhotoLoading}
            />
          </ModalPhotoContainer>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Text
              css={{
                "abyss-text-root": {
                  fontWeight: 700,
                  color: "$app.color.text.primary",
                  fontSize: "16px",
                  lineHeight: "22px",
                },
              }}
            >
              {provider.fullName}
            </Text>
            <Text
              css={{ "abyss-text-root": { color: "$app.color.text.secondary", fontSize: "14px" } }}
            >
              {provider.providerType}
            </Text>
          </div>
        </ModalHeader>

        {provider.providerBio && (
          <ModalSection>
            <Text css={{ "abyss-text-root": { fontSize: "14px", whiteSpace: "pre-line" } }}>
              {provider.providerBio}
            </Text>
          </ModalSection>
        )}

        <Button
          id={`obc-meet-the-team-${provider.providerId}-bio-modal-begin-scheduling-btn`}
          data-id={`obc-meet-the-team-${provider.providerId}-bio-modal-begin-scheduling-btn`}
          data-obc-label={`Begin Scheduling Button in Bio Modal for provider ${provider.fullName}`}
          data-testid={`test-id-provider-team-bio-modal-begin-scheduling-${provider.providerId}`}
          size="md"
          css={{ "abyss-button-root": { width: "100%", marginTop: "8px" } }}
          onClick={handleBookNow}
        >
          Begin scheduling
        </Button>
      </ModalDialog>
    </Card>
  );
};
